import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number in Indian numbering system (lakhs, crores)
 * Examples:
 * - 1234 -> 1,234
 * - 12345 -> 12,345
 * - 123456 -> 1,23,456
 * - 1234567 -> 12,34,567
 * - 12345678 -> 1,23,45,678
 */
export function formatIndianNumber(num: number): string {
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absNum < 1000) {
    return sign + absNum.toFixed(absNum % 1 === 0 ? 0 : 2);
  }

  // Convert to string and split into integer and decimal parts
  const parts = absNum.toFixed(2).split(".");
  const intPart = parts[0] ?? "";
  const decPart = parts[1];

  // Format the integer part with Indian grouping
  // First group: last 3 digits, then groups of 2
  const lastThree = intPart.slice(-3);
  const otherNumbers = intPart.slice(0, -3);

  let formatted = lastThree;
  if (otherNumbers) {
    // Add commas every 2 digits from right to left
    const withCommas = otherNumbers.replace(/\B(?=(\d{2})+(?!\d))/g, ",");
    formatted = withCommas + "," + lastThree;
  }

  // Add decimal part only if it's not .00
  if (decPart && decPart !== "00") {
    formatted += "." + decPart;
  }

  return sign + formatted;
}

/**
 * Format a number as Indian Rupees with proper symbol and formatting
 * Examples:
 * - 1234 -> "₹1,234"
 * - 1234567 -> "₹12,34,567"
 * - -5000 -> "-₹5,000"
 */
export function formatINR(num: number): string {
  const sign = num < 0 ? "-" : "";
  return `${sign}₹${formatIndianNumber(Math.abs(num))}`;
}

/**
 * Format a large number in compact Indian notation (Cr, L, K)
 * Examples:
 * - 1500000 -> "15L"
 * - 15000000 -> "1.5Cr"
 * - 500000 -> "5L"
 * - 50000 -> "50K"
 */
export function formatINRCompact(num: number): string {
  const absNum = Math.abs(num);
  const sign = num < 0 ? "-" : "";

  if (absNum >= 10000000) {
    // Crores (1Cr = 10,000,000)
    const crores = absNum / 10000000;
    return `${sign}₹${crores.toFixed(crores % 1 === 0 ? 0 : 2)}Cr`;
  } else if (absNum >= 100000) {
    // Lakhs (1L = 100,000)
    const lakhs = absNum / 100000;
    return `${sign}₹${lakhs.toFixed(lakhs % 1 === 0 ? 0 : 2)}L`;
  } else if (absNum >= 1000) {
    // Thousands
    const thousands = absNum / 1000;
    return `${sign}₹${thousands.toFixed(thousands % 1 === 0 ? 0 : 1)}K`;
  }

  return `${sign}₹${absNum.toFixed(0)}`;
}

/**
 * Format a percentage with sign
 * Examples:
 * - 12.5 -> "+12.50%"
 * - -3.2 -> "-3.20%"
 */
export function formatPercent(num: number, decimals = 2): string {
  const sign = num > 0 ? "+" : "";
  return `${sign}${num.toFixed(decimals)}%`;
}

/**
 * Format a date as "DD MMM YYYY"
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
